import type { ContextKind, ModuleMeta, Signals, SignalModuleId } from '@fraudeai/schema';

/**
 * Details работает и с локальным отчётом, и с серверным: обоим нужны только
 * список модулей и сырые сигналы. Поэтому пропсы описаны минимально, а не
 * через конкретный тип отчёта.
 */
interface DetailsProps {
  modules: ModuleMeta[];
  signals: Partial<Signals>;
}

const CONTEXT_LABELS: Record<ContextKind, string> = {
  main: 'Главный поток',
  worker: 'Web Worker',
  sharedWorker: 'Shared Worker',
  serviceWorker: 'Service Worker',
  iframe: 'iframe',
  iframeSandboxed: 'iframe в песочнице',
  iframeBlank: 'iframe с srcdoc',
};

const MODULE_LABELS: Record<SignalModuleId, string> = {
  navigator: 'Данные о браузере',
  screen: 'Экран и медиа-запросы',
  family: 'Признаки семейства движка',
  invariants: 'Проверяемые тождества',
  nativecalls: 'Подмена встроенных методов',
  canvas: 'Отрисовка canvas',
  webgl: 'Графика WebGL',
  webgpu: 'Адаптер WebGPU',
  audio: 'Обработка звука',
  fonts: 'Установленные шрифты',
  rects: 'Геометрия элементов',
  contexts: 'Сравнение контекстов',
  natives: 'Целостность функций',
  engine: 'Движок JavaScript',
  automation: 'Признаки автоматизации',
  os: 'Маркеры системы',
  locale: 'Время и локаль',
  storage: 'Хранилища',
  media: 'Медиа-устройства',
  geolocation: 'Геолокация',
  permissions: 'Разрешения',
  network: 'Сеть (клиентская часть)',
  cores: 'Параллелизм процессора',
  probes: 'Внешние пробы (UDP, QUIC, DNS)',
};

/** Наглядная таблица: одна строка — одно поле, одна колонка — один контекст. */
function ContextMatrix({ signals }: DetailsProps) {
  const contexts = signals.contexts;
  if (!contexts) return null;

  const kinds = (Object.keys(contexts.probes) as ContextKind[]).filter(
    (kind) => contexts.probes[kind],
  );
  if (kinds.length === 0) return null;

  // Показываем только те поля, где хоть где-то есть значение, — иначе
  // таблица заполнится прочерками и потеряет читаемость.
  const fields = [
    'userAgent',
    'platform',
    'uaDataPlatform',
    'hardwareConcurrency',
    'deviceMemory',
    'languages',
    'timezone',
    'webglRenderer',
    'canvasHash',
    'mathSignature',
    'errorSignature',
    'webdriver',
  ] as const;

  const mismatched = new Set(contexts.mismatches.map((m) => m.field));

  const render = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) return value.join(', ');
    const text = String(value);
    return text.length > 46 ? `${text.slice(0, 44)}…` : text;
  };

  return (
    <section>
      <h3>Сравнение контекстов</h3>
      <p className="hint">
        Один и тот же вопрос, заданный в разных контекстах браузера. Настоящий браузер
        обязан отвечать одинаково везде — подсвеченные строки означают, что подмена
        доехала не до всех.
      </p>
      <div className="scroll-x">
        <table>
          <thead>
            <tr>
              <th>Сигнал</th>
              {kinds.map((kind) => (
                <th key={kind}>{CONTEXT_LABELS[kind]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const anyValue = kinds.some((kind) => contexts.probes[kind]?.[field] != null);
              if (!anyValue) return null;
              const bad = mismatched.has(field);
              return (
                <tr key={field}>
                  <td className="name" style={bad ? { color: 'var(--fraudulent)' } : undefined}>
                    {field}
                  </td>
                  {kinds.map((kind) => (
                    <td key={kind} style={bad ? { color: 'var(--fraudulent)' } : undefined}>
                      {render(contexts.probes[kind]?.[field])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {contexts.unavailable.length > 0 && (
        <p className="hint" style={{ marginTop: 10 }}>
          Недоступно для проверки: {contexts.unavailable.map((k) => CONTEXT_LABELS[k]).join(', ')}.
        </p>
      )}
    </section>
  );
}

function ModuleTable({ modules }: DetailsProps) {
  return (
    <section>
      <h3>Выполнение проверок</h3>
      <p className="hint">
        Модуль, который не отработал, — это не «чисто», а «не проверено». Поэтому доля
        выполненных проверок ограничивает итоговый скор сверху.
      </p>
      <div className="scroll-x">
        <table>
          <thead>
            <tr>
              <th>Проверка</th>
              <th>Статус</th>
              <th>Время</th>
              <th>Примечание</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((module) => (
              <tr key={module.id}>
                <td className="name">{MODULE_LABELS[module.id] ?? module.id}</td>
                <td
                  className={
                    !module.ok ? 'status-fail' : module.partial ? 'status-partial' : 'status-ok'
                  }
                >
                  {!module.ok ? 'ошибка' : module.partial ? 'частично' : 'выполнено'}
                </td>
                <td>{module.ms} мс</td>
                <td>{module.error ?? (module.partial ? 'часть сигналов недоступна' : '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SignalExplorer({ signals }: DetailsProps) {
  const entries = Object.entries(signals) as [SignalModuleId, unknown][];

  return (
    <section className="explorer">
      <h3>Сырые сигналы</h3>
      <p className="hint">
        Всё, что было измерено, без обработки. Полезно, чтобы проверить выводы чекера
        самостоятельно.
      </p>
      {entries.map(([id, value]) => (
        <details key={id}>
          <summary>
            {MODULE_LABELS[id] ?? id}
            <code>{id}</code>
          </summary>
          <pre>{JSON.stringify(value, null, 2)}</pre>
        </details>
      ))}
    </section>
  );
}

export function Details(props: DetailsProps) {
  return (
    <>
      <ContextMatrix {...props} />
      <ModuleTable {...props} />
      <SignalExplorer {...props} />
    </>
  );
}
